import React, { useEffect, useState } from 'react';
import { Drawer, Box, Typography, TextField, Button, IconButton, FormControl, InputLabel, Select, MenuItem, FormHelperText, Divider, useTheme, useMediaQuery, InputAdornment, CircularProgress, Tooltip, Alert, Fade, Autocomplete, ListItemIcon, ListItemText, InputBase, Chip, Stack, Checkbox } from '@mui/material';
import { Icon } from '@iconify/react';
import { VALID_MTOS, type ValidMTO, VALID_SUB_AGENTS } from '../utils/constants';
// Country data with flags, ISO codes, and dial codes
const COUNTRIES = [{
  name: 'Ghana',
  iso: 'GH',
  dialCode: '+233',
  flag: '🇬🇭',
  phoneFormat: '(+233) XX XXX XXXX'
}, {
  name: 'Nigeria',
  iso: 'NG',
  dialCode: '+234',
  flag: '🇳🇬',
  phoneFormat: '(+234) XXX XXX XXXX'
}, {
  name: 'Senegal',
  iso: 'SN',
  dialCode: '+221',
  flag: '🇸🇳',
  phoneFormat: '(+221) XX XXX XXXX'
}, {
  name: 'Uganda',
  iso: 'UG',
  dialCode: '+256',
  flag: '🇺🇬',
  phoneFormat: '(+256) XXX XXX XXXX'
}, {
  name: 'Kenya',
  iso: 'KE',
  dialCode: '+254',
  flag: '🇰🇪',
  phoneFormat: '(+254) XXX XXX XXXX'
}, {
  name: "Côte d'Ivoire",
  iso: 'CI',
  dialCode: '+225',
  flag: '🇨🇮',
  phoneFormat: '(+225) XX XX XX XXXX'
}, {
  name: 'Togo',
  iso: 'TG',
  dialCode: '+228',
  flag: '🇹🇬',
  phoneFormat: '(+228) XX XXX XXXX'
}, {
  name: 'Cameroon',
  iso: 'CM',
  dialCode: '+237',
  flag: '🇨🇲',
  phoneFormat: '(+237) X XXXX XXXX'
}, {
  name: 'Benin',
  iso: 'BJ',
  dialCode: '+229',
  flag: '🇧🇯',
  phoneFormat: '(+229) XX XX XXXX'
}];
// Integration modes
const INTEGRATION_MODES = [{
  value: 'push',
  label: 'Push'
}, {
  value: 'pull',
  label: 'Pull'
}, {
  value: 'file',
  label: 'File'
}];
// Transaction types
const TRANSACTION_TYPES = [{
  value: 'otc',
  label: 'OTC',
  tooltip: 'Over The Counter'
}, {
  value: 'ft',
  label: 'FT',
  tooltip: 'Funds Transfer'
}, {
  value: 'ibft',
  label: 'IBFT',
  tooltip: 'Interbank Funds Transfer'
}];
interface PartnerFormProps {
  open: boolean;
  partner: any | null;
  onClose: () => void;
  onSave: (partnerData: any) => void;
}
interface FormData {
  partnerName: ValidMTO | string;
  country: string;
  countryObject: any | null;
  address: string;
  pointOfContact: string;
  contactNumber: string;
  settlementAccount: string;
  accountTitle?: string;
  integrationMode: string;
  transactionsAllowed: string[];
  configuredSubagents: string[];
  email?: string;
}
interface FormErrors {
  partnerName?: string;
  country?: string;
  address?: string;
  pointOfContact?: string;
  contactNumber?: string;
  settlementAccount?: string;
  integrationMode?: string;
  transactionsAllowed?: string;
  configuredSubagents?: string;
  email?: string;
}
// Settlement Account validation regex
const SETTLEMENT_ACCOUNT_REGEX = /^[A-Z0-9-]{5,30}$/;
// Email validation regex
const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
const PartnerFormDrawer: React.FC<PartnerFormProps> = ({
  open,
  partner,
  onClose,
  onSave
}) => {
  const [formData, setFormData] = useState<FormData>({
    partnerName: '',
    country: '',
    countryObject: null,
    address: '',
    pointOfContact: '',
    contactNumber: '',
    settlementAccount: '',
    accountTitle: '',
    integrationMode: '',
    transactionsAllowed: [],
    configuredSubagents: [],
    email: ''
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isFetchingTitle, setIsFetchingTitle] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [showAccountTitle, setShowAccountTitle] = useState(false);
  const [ibanValid, setIbanValid] = useState(false);
  const [phoneFieldFocused, setPhoneFieldFocused] = useState(false);
  const [countrySearchText, setCountrySearchText] = useState('');
  const [subagentSearchText, setSubagentSearchText] = useState('');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  useEffect(() => {
    if (partner) {
      // Find country object from name
      const countryObj = COUNTRIES.find(c => c.name === partner.country) || null;
      setFormData({
        partnerName: partner.partnerName || '',
        country: partner.country || '',
        countryObject: countryObj,
        address: partner.address || '',
        pointOfContact: partner.pointOfContact || '',
        contactNumber: partner.contactNumber || '',
        settlementAccount: partner.settlementAccount || '',
        accountTitle: partner.accountTitle || '',
        integrationMode: partner.integrationMode || '',
        transactionsAllowed: partner.transactionsAllowed || [],
        configuredSubagents: partner.configuredSubagents || [],
        email: partner.email || ''
      });
      // If partner has account title, show the field
      if (partner.accountTitle) {
        setShowAccountTitle(true);
        validateSettlementAccount(partner.settlementAccount || '');
      }
    } else {
      setFormData({
        partnerName: '',
        country: '',
        countryObject: null,
        address: '',
        pointOfContact: '',
        contactNumber: '',
        settlementAccount: '',
        accountTitle: '',
        integrationMode: '',
        transactionsAllowed: [],
        configuredSubagents: [],
        email: ''
      });
      setShowAccountTitle(false);
    }
    setErrors({});
    setFetchError(null);
  }, [partner, open]);
  // Effect to prefill dial code when country changes and phone field is empty
  useEffect(() => {
    if (formData.countryObject && (!formData.contactNumber || formData.contactNumber === '' || formData.contactNumber.startsWith('+') && formData.contactNumber.length <= 4)) {
      setFormData(prev => ({
        ...prev,
        contactNumber: formData.countryObject.dialCode + ' '
      }));
    }
  }, [formData.countryObject]);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | {
    name?: string;
    value: unknown;
  }>) => {
    const {
      name,
      value
    } = e.target;
    if (name) {
      // Special handling for settlementAccount
      if (name === 'settlementAccount') {
        // Format account: convert to uppercase
        const formattedValue = String(value).toUpperCase();
        setFormData({
          ...formData,
          [name]: formattedValue,
          // Clear account title if account is edited after a successful fetch
          ...(showAccountTitle && {
            accountTitle: ''
          })
        });
        // Validate the account and update the button state
        validateSettlementAccount(formattedValue);
        // If user changes account after fetching title, reset the title
        if (showAccountTitle) {
          setShowAccountTitle(false);
          setFetchError(null);
        }
      } else {
        setFormData({
          ...formData,
          [name]: value
        });
      }
      // Clear error when field is edited
      if (errors[name as keyof FormErrors]) {
        setErrors({
          ...errors,
          [name]: undefined
        });
      }
    }
  };
  const handleCountryChange = (event: any, newValue: any) => {
    setFormData({
      ...formData,
      country: newValue ? newValue.name : '',
      countryObject: newValue,
      // Clear phone number if country changes to avoid validation issues
      ...(formData.contactNumber && {
        contactNumber: newValue ? newValue.dialCode + ' ' : ''
      })
    });
    // Clear country error if exists
    if (errors.country) {
      setErrors({
        ...errors,
        country: undefined
      });
    }
  };
  const handleTransactionsAllowedChange = (event: React.ChangeEvent<{
    value: unknown;
  }>) => {
    const value = event.target.value as string[];
    setFormData({
      ...formData,
      transactionsAllowed: value
    });
    // Clear error if at least one transaction type is selected
    if (errors.transactionsAllowed && value.length > 0) {
      setErrors({
        ...errors,
        transactionsAllowed: undefined
      });
    }
  };
  const handleSubagentsChange = (event: any, newValue: string[]) => {
    setFormData({
      ...formData,
      configuredSubagents: newValue
    });
    // Clear error if at least one subagent is selected
    if (errors.configuredSubagents && newValue.length > 0) {
      setErrors({
        ...errors,
        configuredSubagents: undefined
      });
    }
  };
  const handlePhoneFocus = () => {
    setPhoneFieldFocused(true);
    // Prefill dial code if field is empty and country is selected
    if ((!formData.contactNumber || formData.contactNumber === '') && formData.countryObject) {
      setFormData({
        ...formData,
        contactNumber: formData.countryObject.dialCode + ' '
      });
    }
  };
  const handlePhoneBlur = () => {
    setPhoneFieldFocused(false);
  };
  const validateSettlementAccount = (account: string): boolean => {
    // Check if it's a valid settlement account format
    const isValid = SETTLEMENT_ACCOUNT_REGEX.test(account);
    setIbanValid(isValid);
    // Set error message if invalid
    if (account && !isValid) {
      setErrors({
        ...errors,
        settlementAccount: 'Enter a valid settlement account (e.g., 1110-GH-USD)'
      });
    } else {
      // Clear error if valid or empty
      const updatedErrors = {
        ...errors
      };
      delete updatedErrors.settlementAccount;
      setErrors(updatedErrors);
    }
    return isValid;
  };
  const handleFetchTitle = () => {
    setIsFetchingTitle(true);
    setFetchError(null);
    // Simulate API call with timeout
    setTimeout(() => {
      // 90% success rate for demo purposes
      const isSuccess = Math.random() < 0.9;
      if (isSuccess) {
        // Success case
        const mockTitles = ['ACME CORPORATION LTD.', 'GLOBAL EXCHANGE SERVICES', 'FIRST TRADING COMPANY', 'INTERNATIONAL REMITTANCE LLC', 'UNITED FINANCIAL SERVICES'];
        const randomTitle = mockTitles[Math.floor(Math.random() * mockTitles.length)];
        setFormData({
          ...formData,
          accountTitle: randomTitle
        });
        setShowAccountTitle(true);
        // Show toast (in a real app, you would use a toast library)
        console.log('Account title fetched successfully');
      } else {
        // Error case
        setFetchError('Title fetch failed. Please verify IBAN or try again.');
      }
      setIsFetchingTitle(false);
    }, 2500); // 2.5 seconds delay
  };
  const validate = (): boolean => {
    const newErrors: FormErrors = {};
    if (!formData.partnerName.trim()) {
      newErrors.partnerName = 'Partner name (MTO) is required';
    }
    if (!formData.country) {
      newErrors.country = 'Country is required';
    }
    if (!formData.address.trim()) {
      newErrors.address = 'Address is required';
    }
    if (!formData.pointOfContact.trim()) {
      newErrors.pointOfContact = 'Point of contact is required';
    }
    if (!formData.settlementAccount.trim()) {
      newErrors.settlementAccount = 'Settlement account is required';
    } else if (!validateSettlementAccount(formData.settlementAccount)) {
      newErrors.settlementAccount = 'Enter a valid settlement account (e.g., 1110-GH-USD)';
    }
    // Email validation (if provided)
    if (formData.email && !EMAIL_REGEX.test(formData.email)) {
      newErrors.email = 'Enter a valid email address';
    }
    // Integration Mode validation
    if (!formData.integrationMode) {
      newErrors.integrationMode = 'Integration mode is required';
    }
    // Transactions Allowed validation
    if (!formData.transactionsAllowed.length) {
      newErrors.transactionsAllowed = 'At least one transaction type is required';
    }
    // Configured Subagents validation
    if (!formData.configuredSubagents.length) {
      newErrors.configuredSubagents = 'At least one subagent is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = () => {
    if (validate()) {
      onSave({
        ...formData,
        id: partner?.id
      });
    }
  };
  return <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{
    sx: {
      width: {
        xs: '100%',
        sm: 400
      },
      p: 0
    }
  }} data-id="element-1182">
      <Box sx={{
      p: 3
    }} data-id="element-1183">
        <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 3
      }} data-id="element-1184">
          <Typography variant="h6" data-id="element-1185">
            {partner ? 'Edit Partner' : 'Add New Partner'}
          </Typography>
          <IconButton onClick={onClose} size="small" data-id="element-1186">
            <Icon icon="lucide:x" width={18} height={18} data-id="element-1187" />
          </IconButton>
        </Box>
        <Box component="form" noValidate autoComplete="off" sx={{
        mt: 2
      }} data-id="element-1188">
          {/* Partner Name (MTO) Dropdown */}
          <FormControl fullWidth margin="normal" error={!!errors.partnerName} required data-id="element-1189">
            <InputLabel id="partner-name-label" data-id="element-1190">Partner Name (MTO)</InputLabel>
            <Select labelId="partner-name-label" name="partnerName" value={formData.partnerName} onChange={handleChange} label="Partner Name (MTO)" data-id="element-1191">
              {VALID_MTOS.map(mto => <MenuItem key={mto} value={mto} data-id="element-1192">
                  {mto}
                </MenuItem>)}
            </Select>
            {errors.partnerName && <FormHelperText data-id="element-1193">{errors.partnerName}</FormHelperText>}
          </FormControl>
          {/* Integration Mode Dropdown */}
          <FormControl fullWidth margin="normal" error={!!errors.integrationMode} required data-id="element-1194">
            <InputLabel id="integration-mode-label" data-id="element-1195">
              Integration Mode
            </InputLabel>
            <Select labelId="integration-mode-label" name="integrationMode" value={formData.integrationMode} onChange={handleChange} label="Integration Mode" data-id="element-1196">
              {INTEGRATION_MODES.map(mode => <MenuItem key={mode.value} value={mode.value} data-id="element-1197">
                  {mode.label}
                </MenuItem>)}
            </Select>
            {errors.integrationMode && <FormHelperText data-id="element-1198">{errors.integrationMode}</FormHelperText>}
          </FormControl>
          {/* Transactions Allowed Multi-select */}
          <FormControl fullWidth margin="normal" error={!!errors.transactionsAllowed} required data-id="element-1199">
            <InputLabel id="transactions-allowed-label" data-id="element-1200">
              <Box sx={{
              display: 'flex',
              alignItems: 'center'
            }} data-id="element-1201">
                Transactions Allowed
                <Tooltip title="Select transaction types that this partner can process" data-id="element-1202">
                  <Icon icon="lucide:help-circle" width={16} height={16} style={{
                  marginLeft: '4px',
                  opacity: 0.7
                }} data-id="element-1203" />
                </Tooltip>
              </Box>
            </InputLabel>
            <Select labelId="transactions-allowed-label" multiple name="transactionsAllowed" value={formData.transactionsAllowed} onChange={handleTransactionsAllowedChange} label="Transactions Allowed" renderValue={selected => <Box sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 0.5
          }} data-id="element-1205">
                  {(selected as string[]).map(value => {
              const transaction = TRANSACTION_TYPES.find(t => t.value === value);
              return <Chip key={value} label={transaction?.label || value} size="small" sx={{
                backgroundColor: 'rgba(14, 165, 233, 0.1)',
                color: '#0EA5E9',
                borderRadius: '4px',
                fontWeight: 600,
                fontSize: '0.7rem'
              }} data-id="element-1206" />;
            })}
                </Box>} data-id="element-1204">
              {TRANSACTION_TYPES.map(type => <MenuItem key={type.value} value={type.value} data-id="element-1207">
                  <Checkbox checked={formData.transactionsAllowed.indexOf(type.value) > -1} data-id="element-1208" />
                  <ListItemText primary={<Tooltip title={type.tooltip} data-id="element-1210">
                        <span data-id="element-1211">{type.label}</span>
                      </Tooltip>} data-id="element-1209" />
                </MenuItem>)}
            </Select>
            {errors.transactionsAllowed && <FormHelperText data-id="element-1212">{errors.transactionsAllowed}</FormHelperText>}
          </FormControl>
          {/* Configured Subagents Multi-select with Search */}
          <FormControl fullWidth margin="normal" error={!!errors.configuredSubagents} required data-id="element-1213">
            <Typography variant="body2" fontWeight={500} sx={{
            mb: 0.5,
            color: 'text.secondary',
            fontSize: '0.8rem'
          }} data-id="element-1214">
              Configured Subagents *
            </Typography>

            {/* Selected subagent pills - rendered OUTSIDE and ABOVE the dropdown */}
            {formData.configuredSubagents.length > 0 && <Box sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 0.75,
            mb: 1
          }} data-id="element-1215">
                {formData.configuredSubagents.map(subagent => <Chip key={subagent} label={subagent} size="small" onDelete={() => {
              const updated = formData.configuredSubagents.filter(s => s !== subagent);
              setFormData({
                ...formData,
                configuredSubagents: updated
              });
              if (errors.configuredSubagents && updated.length > 0) {
                setErrors({
                  ...errors,
                  configuredSubagents: undefined
                });
              }
            }} deleteIcon={<Icon icon="lucide:x" width={14} height={14} data-id="element-1217" />} sx={{
              backgroundColor: '#F1F5F9',
              color: '#334155',
              borderRadius: '6px',
              fontWeight: 500,
              fontSize: '0.75rem',
              height: 28,
              '& .MuiChip-deleteIcon': {
                color: '#94A3B8',
                fontSize: '14px',
                '&:hover': {
                  color: '#64748B'
                }
              }
            }} data-id="element-1216" />)}
              </Box>}

            {/* Searchable checkbox dropdown */}
            <Select multiple value={formData.configuredSubagents} onChange={e => {
            const value = e.target.value as string[];
            setFormData({
              ...formData,
              configuredSubagents: value
            });
            if (errors.configuredSubagents && value.length > 0) {
              setErrors({
                ...errors,
                configuredSubagents: undefined
              });
            }
          }} displayEmpty renderValue={() => {
            if (formData.configuredSubagents.length === 0) {
              return <Typography variant="body2" sx={{
                color: '#9CA3AF',
                fontSize: '0.85rem'
              }} data-id="element-1219">
                      Search and select subagents
                    </Typography>;
            }
            return <Typography variant="body2" sx={{
              color: '#64748B',
              fontSize: '0.85rem'
            }} data-id="element-1220">
                    {formData.configuredSubagents.length} subagent
                    {formData.configuredSubagents.length > 1 ? 's' : ''}{' '}
                    selected
                  </Typography>;
          }} MenuProps={{
            PaperProps: {
              sx: {
                maxHeight: 300,
                mt: 0.5
              }
            },
            autoFocus: false
          }} error={!!errors.configuredSubagents} sx={{
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: errors.configuredSubagents ? '#d32f2f' : '#D1D5DB'
            }
          }} data-id="element-1218">
              {/* Search field inside dropdown */}
              <MenuItem disableRipple disableTouchRipple onKeyDown={e => e.stopPropagation()} sx={{
              position: 'sticky',
              top: 0,
              zIndex: 1,
              backgroundColor: '#fff',
              p: 1,
              '&:hover': {
                backgroundColor: '#fff'
              },
              '&.Mui-focusVisible': {
                backgroundColor: '#fff'
              }
            }} data-id="element-1221">
                <TextField size="small" fullWidth placeholder="Search subagents..." value={subagentSearchText} onChange={e => setSubagentSearchText(e.target.value)} onClick={e => e.stopPropagation()} InputProps={{
                startAdornment: <InputAdornment position="start" data-id="element-1223">
                        <Icon icon="lucide:search" width={16} height={16} color="#94A3B8" data-id="element-1224" />
                      </InputAdornment>,
                sx: {
                  height: 36,
                  fontSize: '0.85rem'
                }
              }} data-id="element-1222" />
              </MenuItem>
              {VALID_SUB_AGENTS.filter(agent => agent.toLowerCase().includes(subagentSearchText.toLowerCase())).map(agent => <MenuItem key={agent} value={agent} sx={{
              py: 0.5
            }} data-id="element-1225">
                  <Checkbox checked={formData.configuredSubagents.includes(agent)} size="small" sx={{
                mr: 1
              }} data-id="element-1226" />
                  <ListItemText primary={agent} primaryTypographyProps={{
                fontSize: '0.85rem'
              }} data-id="element-1227" />
                </MenuItem>)}
              {VALID_SUB_AGENTS.filter(agent => agent.toLowerCase().includes(subagentSearchText.toLowerCase())).length === 0 && <MenuItem disabled data-id="element-1228">
                  <Typography variant="body2" color="text.secondary" sx={{
                fontSize: '0.85rem'
              }} data-id="element-1229">
                    No subagents found
                  </Typography>
                </MenuItem>}
            </Select>
            {errors.configuredSubagents && <FormHelperText error data-id="element-1230">
                {errors.configuredSubagents}
              </FormHelperText>}
            {!errors.configuredSubagents && <FormHelperText data-id="element-1231">
                Select subagents that can process transactions for this partner
              </FormHelperText>}
          </FormControl>
          {/* Enhanced Country Selection with Flags and Search */}
          <FormControl fullWidth margin="normal" error={!!errors.country} required data-id="element-1232">
            <Autocomplete id="country-select" options={COUNTRIES} value={formData.countryObject} onChange={handleCountryChange} getOptionLabel={option => option.name} isOptionEqualToValue={(option, value) => option.iso === value.iso} renderOption={(props, option) => <MenuItem {...props} sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }} data-id="element-1234">
                  <Box component="span" sx={{
              fontSize: '1.2rem',
              mr: 1
            }} data-id="element-1235">
                    {option.flag}
                  </Box>
                  <ListItemText primary={option.name} secondary={`(${option.iso}) ${option.dialCode}`} primaryTypographyProps={{
              fontWeight: 500
            }} secondaryTypographyProps={{
              fontSize: '0.75rem'
            }} data-id="element-1236" />
                </MenuItem>} renderInput={params => <TextField {...params} label="Country" required error={!!errors.country} helperText={errors.country} InputProps={{
            ...params.InputProps,
            startAdornment: <>
                        {formData.countryObject && <InputAdornment position="start" data-id="element-1238">
                            <Typography variant="body1" sx={{
                  fontSize: '1.2rem',
                  mr: 1
                }} data-id="element-1239">
                              {formData.countryObject.flag}
                            </Typography>
                          </InputAdornment>}
                        {params.InputProps.startAdornment}
                      </>,
            endAdornment: <>
                        {params.InputProps.endAdornment}
                        <InputAdornment position="end" data-id="element-1240">
                          <Icon icon="lucide:search" width={16} height={16} color="#64748B" data-id="element-1241" />
                        </InputAdornment>
                      </>
          }} data-id="element-1237" />} data-id="element-1233" />
          </FormControl>
          <TextField fullWidth margin="normal" label="Address" name="address" value={formData.address} onChange={handleChange} error={!!errors.address} helperText={errors.address} required multiline rows={2} data-id="element-1242" />
          <TextField fullWidth margin="normal" label="Point of Contact" name="pointOfContact" value={formData.pointOfContact} onChange={handleChange} error={!!errors.pointOfContact} helperText={errors.pointOfContact} required data-id="element-1243" />
          {/* Email Input with validation */}
          <TextField fullWidth margin="normal" label="Email" name="email" type="email" value={formData.email || ''} onChange={handleChange} error={!!errors.email} helperText={errors.email} placeholder="contact@example.com" data-id="element-1244" />
          {/* Enhanced Phone Input with Country-based Formatting */}
          <Tooltip title={formData.countryObject ? `Format: ${formData.countryObject.phoneFormat}` : 'Select a country for format guidance'} placement="top" arrow data-id="element-1245">
            <TextField fullWidth margin="normal" label="Contact Number" name="contactNumber" value={formData.contactNumber} onChange={handleChange} onFocus={handlePhoneFocus} onBlur={handlePhoneBlur} error={!!errors.contactNumber} helperText={errors.contactNumber || 'Format updates with selected country'} required placeholder={formData.countryObject ? formData.countryObject.phoneFormat : 'Select a country first'} InputProps={{
            startAdornment: formData.countryObject && phoneFieldFocused ? <InputAdornment position="start" data-id="element-1247">
                      <Typography variant="body2" color="text.secondary" data-id="element-1248">
                        {formData.countryObject.flag}
                      </Typography>
                    </InputAdornment> : null
          }} data-id="element-1246" />
          </Tooltip>
          {/* IBAN Settlement Account Field Group */}
          <Box sx={{
          mt: 3,
          mb: 2,
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'flex-start' : 'flex-end',
          gap: isMobile ? 2 : 1
        }} data-id="element-1249">
            <TextField fullWidth label="Settlement Account (IBAN)" name="settlementAccount" placeholder="e.g., GB29NWBK60161331926819" value={formData.settlementAccount} onChange={handleChange} error={!!errors.settlementAccount} helperText={errors.settlementAccount || 'Enter a valid IBAN'} required disabled={isFetchingTitle} InputProps={{
            sx: {
              textTransform: 'uppercase'
            }
          }} sx={{
            flex: isMobile ? 1 : 3,
            width: isMobile ? '100%' : 'auto',
            '& .MuiOutlinedInput-root': {
              '&:hover .MuiOutlinedInput-notchedOutline': {
                boxShadow: '0 0 0 3px rgba(14, 165, 233, 0.1)'
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#0EA5E9',
                boxShadow: '0 0 0 3px rgba(14, 165, 233, 0.1)'
              }
            }
          }} data-id="element-1250" />
            <Tooltip title="Validate and fetch account title" data-id="element-1251">
              <span data-id="element-1252">
                <Button variant="outlined" disabled={!ibanValid || isFetchingTitle} onClick={handleFetchTitle} sx={{
                minWidth: '120px',
                width: isMobile ? '100%' : 'auto',
                height: '56px',
                borderColor: '#D1D5DB',
                color: '#0EA5E9',
                '&:hover': {
                  borderColor: '#0EA5E9',
                  backgroundColor: 'rgba(14, 165, 233, 0.04)',
                  transform: 'translateY(-1px)'
                },
                '&.Mui-disabled': {
                  borderColor: '#E5E7EB'
                }
              }} aria-live="polite" data-id="element-1253">
                  {isFetchingTitle ? <>
                      <CircularProgress size={16} sx={{
                    mr: 1,
                    color: '#0EA5E9'
                  }} data-id="element-1254" />
                      Fetching...
                    </> : 'Fetch Title'}
                </Button>
              </span>
            </Tooltip>
          </Box>
          {/* Fetch error message */}
          {fetchError && <Fade in={!!fetchError} data-id="element-1255">
              <Alert severity="error" icon={<Icon icon="lucide:alert-circle" width={18} height={18} data-id="element-1257" />} sx={{
            mb: 2
          }} onClose={() => setFetchError(null)} data-id="element-1256">
                {fetchError}
              </Alert>
            </Fade>}
          {/* Account Title Field (shown only after successful fetch) */}
          {showAccountTitle && <Fade in={showAccountTitle} data-id="element-1258">
              <TextField fullWidth margin="normal" label="Account Title" value={formData.accountTitle || ''} disabled InputProps={{
            readOnly: true,
            endAdornment: <InputAdornment position="end" data-id="element-1260">
                      <Icon icon="lucide:check-circle" width={18} height={18} color="#10B981" data-id="element-1261" />
                    </InputAdornment>
          }} sx={{
            mb: 2,
            '& .MuiInputBase-input.Mui-disabled': {
              WebkitTextFillColor: '#0F172A',
              fontWeight: 500
            },
            '& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline': {
              borderColor: '#D1D5DB',
              borderStyle: 'dashed'
            }
          }} data-id="element-1259" />
            </Fade>}
        </Box>
      </Box>
      <Box sx={{
      position: 'sticky',
      bottom: 0,
      backgroundColor: 'background.paper',
      p: 3,
      borderTop: '1px solid #E5E7EB'
    }} data-id="element-1262">
        <Box sx={{
        display: 'flex',
        justifyContent: 'flex-end',
        gap: 2
      }} data-id="element-1263">
          <Button variant="outlined" onClick={onClose} data-id="element-1264">
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSubmit} data-id="element-1265">
            Save
          </Button>
        </Box>
      </Box>
    </Drawer>;
};
export default PartnerFormDrawer;